import {View} from "react-native";
import {BlockMembershipRequest, EditMembershipRequest, GroupInviteRequest, GroupRoles} from "@/lib/types";
import {useLocalSearchParams, useRouter} from "expo-router";
import {dataRetryPolicy, fetchBackend, fetchBackendNoContent} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import Page from "@/lib/components/Page";
import ErrorPage from "@/lib/components/ErrorPage";
import SpinnerPage from "@/lib/components/SpinnerPage";
import Header from "@/lib/components/Header";
import Badge from "@/lib/components/Badge";
import {useEffect, useMemo, useState} from "react";
import Radio from "@/lib/components/Radio";
import ActionButton from "@/lib/components/ActionButton";
import ErrorMessage from "@/lib/components/ErrorMessage";
import {useGroup} from "@/lib/components/GroupProvider";
import Split from "@/lib/components/Split";
import {Color} from "@/lib/styles";
import Message from "@/lib/components/Message";
import Nav from "@/lib/components/Nav";
import {useQuery} from "@tanstack/react-query";

export default function RoleEditPage() {
    const {group_id, username} = useLocalSearchParams();
    const {session} = useSession();
    const [roles, setRoles] = useState<GroupRoles | null>(null);
    const [busy, setBusy] = useState(false);
    const [blocking, setBlocking] = useState(false);
    const [saveError, setSaveError] = useState<Error | null>(null);
    const router = useRouter();

    const {group, groupLoading, groupError, refreshGroup} = useGroup();

    const {data, isPending, error} = useQuery<GroupRoles>({
        queryKey: ['group', group_id, 'membership', username, session],
        queryFn: () => fetchBackend(`/group/${group_id}/membership/${username}`, "GET", session),
        retry: dataRetryPolicy,
    })

    const subject = group?.members.find(p => p.avatar.username === username) || null;

    useEffect(() => {
        if (! data) {
            return;
        }
        setRoles(data);
    }, [data])

    const header = useMemo(() => {
        if (!subject || !group) {
            return null;
        }
        return (
            <View style={{gap: 8}}>
                <Header title={subject.avatar.gecos} icon={"edit"}/>

                <View style={{alignItems: "flex-start"}}>
                    <Badge title={group.name}
                           color={group.color}
                           contrastColor={group.contrast_color}
                    />
                </View>
            </View>
        )
    }, [subject, group]);

    if (groupError) {
        return <ErrorPage error={groupError}/>
    } else if (error) {
        return <ErrorPage error={error}/>
    } else if (groupLoading || isPending || !group || !roles || !data) {
        return <SpinnerPage/>
    }

    async function saveRoles() {
        if (!group || !subject || !roles) {
            return;
        }
        setSaveError(null);
        setBusy(true);
        let payload: EditMembershipRequest = {
            user_id: subject.avatar.id,
            roles,
        }
        fetchBackendNoContent(`/group/${group.group_id}/membership`, "PUT", session, payload).then(async () => {
            await refreshGroup();
            router.dismiss();
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setBusy(false);
        });
    }

    async function unblockUser() {
        if (!group || !subject) {
            return;
        }
        setSaveError(null);
        setBusy(true);
        const payload = new GroupInviteRequest([subject.avatar.id]);
        fetchBackendNoContent(`/group/${group.group_id}/invite`, "POST", session, payload).then(async () => {
            await refreshGroup();
            router.dismiss();
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setBusy(false);
        });
    }

    async function blockUser() {
        if (!group || !subject) {
            return;
        }
        setSaveError(null);
        setBlocking(true);
        let payload: BlockMembershipRequest = {
            user_id: subject.avatar.id,
        }
        fetchBackendNoContent(`/group/${group.group_id}/membership`, "DELETE", session, payload).then(async () => {
            await refreshGroup();
            router.dismiss();
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setBlocking(false);
        });
    }

    if (!data.is_blocked) {
        return (
            <Page scrollable={true}>
                <View style={{gap: 24}}>

                    {header}

                    <View style={{gap: 8}}>
                        <Header title={"Angi rolle"}/>
                        <Radio selected={roles.is_admin}
                               onPress={() => setRoles({...roles, is_admin: true, is_moderator: false})}
                               title={"Administrator"}
                               description={"Har alle rettigheter og kan i tillegg bestemme roller."}
                               checkbox={false}
                        />
                        <Radio selected={roles.is_moderator}
                               onPress={() => setRoles({...roles, is_admin: false, is_moderator: true})}
                               title={"Moderator"}
                               description={"Kan invitere og utestenge, starte diskusjoner, samt redigere samfunnet."}
                               checkbox={false}
                        />
                        <Radio selected={!roles.is_admin && !roles.is_moderator}
                               onPress={() => setRoles({...roles, is_admin: false, is_moderator: false})}
                               title={"Medlem"}
                               description={"Ingen ekstra rettigheter utover samfunnets innstillinger."}
                               checkbox={false}
                        />
                    </View>

                    <View style={{gap: 8}}>
                        <Header title={"Ekstra tilganger"}/>
                        <Radio selected={roles.can_invite}
                               onPress={() => setRoles({...roles, can_invite: !roles.can_invite})}
                               title={"Invitasjon"}
                               description={"Medlemmet kan invitere venner til samfunnet"}
                               checkbox={true}
                        />
                        <Radio selected={roles.can_post}
                               onPress={() => setRoles({...roles, can_post: !roles.can_post})}
                               title={"Chat"}
                               description={"Medlemmet kan starte diskusjonstråder"}
                               checkbox={true}
                        />
                    </View>

                    {saveError && <ErrorMessage error={saveError}/>}

                    <Split left={
                        <ActionButton onPress={blockUser} icon={"cancel"} title={"Blokker"} busy={blocking} color={Color.Red}/>
                    } right={
                        <ActionButton onPress={saveRoles} icon={"checkmark"} title={"Lagre"} busy={busy}/>
                    }/>
                </View>
            </Page>
        )
    }

    return (
        <Page scrollable={true}>
            <View style={{gap: 24}}>

                {header}

                <View style={{gap: 8}}>
                    <Header title={"Brukeren er forvist"}/>
                    <Message text={"Du kan fjerne blokkeringen for å invitere vedkommende tilbake i samfunnet."}/>
                </View>

                {saveError && <ErrorMessage error={saveError}/>}

                <Nav>
                    <ActionButton onPress={unblockUser} icon={"cancel"} title={"Inviter inn igjen"} busy={busy}/>
                </Nav>
            </View>
        </Page>
    )
}